import { createContext, useContext, ReactNode, useRef, useEffect } from 'react'
import { isCancelledError, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { toast } from 'sonner'
import { useLocation, useNavigate } from 'react-router-dom'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useAuthStore } from '@/store/authStore'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { installAlertSoundUnlock, isAlertSoundReady, playAlertSound, stopCriticalSound } from '@/lib/alertSound'  // #295
import {
  announceCriticalSeen, isSoundLeader, onChimeRequested, onCriticalSeen, onLogoutAnnounced, requestChime, stopLeadership,
} from '@/lib/soundLeader'  // #294 redesign, #328
import { isCriticalSounding } from '@/lib/criticalSoundState'  // #294 redesign
import { useDisplayPrefs } from '@/store/displayPrefsStore'  // #294 redesign
import { shouldShowDesktopAlert, showDesktopAlert } from '@/lib/desktopAlerts'  // #297
import { desktopAlertOf } from '@/components/notifications/desktopAlert'  // #297
import { eventOpenTarget } from '@/lib/navigation'  // #297
import { eventsApi } from '@/api/endpoints'
import { muteReason } from '@/lib/pushBell'  // #138, #337
import { usePushMutes } from '@/hooks/usePushMutes'  // #337
import { isCriticalEvent } from '@/lib/criticalEvents'  // #294
import { queryKeys, queryOptions } from '@/config/queryConfig'
import {
  REPLAY_TOAST_CAP, forgetAnnounced, liveLog, pickMissed, rememberAnnounced, type LiveStatus,
} from '@/lib/liveLink'  // #328
import { fetchVersionJson, showNewBuildToast, startBuildWatch } from '@/lib/buildVersion'  // #336

interface WebSocketContextValue {
  isConnected: boolean
  lastEvent: any
  status: LiveStatus  // #328
  lastDisconnectAt: number | null  // #328
  restart: (reason: string) => void  // #328
  reconnect: () => void
  disconnect: () => void
}

/** #328 — what the provider decided for one event (the diagnostics trail logs it). */
type AnnounceDecision = 'shown' | 'muted-category' | 'muted-camera' | 'critical'

/** #328 (owner ruling b) — the last event ids the sound leader chimed for, oldest dropped first: one chime per
 *  event, whichever tabs asked for it. */
const CHIMED_IDS_KEPT = 200

/** #328 — the last event ids this tab has handled (announced, silenced by the mutes, or counted in a replay
 *  summary), oldest dropped first. Every path checks it, so an event is never announced twice, whichever path
 *  (the socket, a replay, the poll) saw it first, and an event the socket delivered is never taken for a missed one. */
const HANDLED_IDS_KEPT = 500

/** #328 — the poll restarts a deaf socket at most this often (a server that is really behind cannot loop it). */
const DEAF_RESTART_GAP_MS = 5 * 60_000

/** #328 (round 4) — the last event ids the live socket delivered (announced, muted or already handled), oldest
 *  dropped first: an event the poll found first is no evidence of a deaf socket once the socket has delivered it. */
const SOCKET_IDS_KEPT = 500

/** #328 (round 4) — at most this many events the poll found first wait for their judgement (a page holds 20). */
const POLL_FIRST_KEPT = 100

/** #328 (round 4) — an event the poll found first is evidence of a deaf socket only when the next poll, at least
 *  this much later by this tab's own clock, still shows it and the socket has not delivered it meanwhile (the
 *  handler broadcasts right after the insert, so a live message may still be on its way). No server time is read,
 *  so a browser clock far off the server's changes nothing. Announcing never waits for this. */
const DEAF_EVIDENCE_WAIT_MS = 30_000

/** #328 (round 4) — no deaf-socket evidence this long after a (re)connect: the replay and the new socket's first
 *  messages are still settling, and an event missed during the outage is no fault of the new socket. */
const CONNECT_QUIET_MS = 30_000

/** #328 (round 4, R2) — the Dashboard's list of recent events (motion-only left out, #304): the poll, the replay
 *  and the page start all read this one list. */
const fetchRecentEvents = () => eventsApi.getAll({ hide_motion: true }, 1)

/** #328 (round 5) — the replay reads the request the socket's open started. A live message landing meanwhile makes
 *  the hook refresh the list, and React Query replaces that request (the one the replay joined is cancelled): the
 *  replay joins the replacement, at most this many times, so a drop's missed events are not lost behind the floor. */
const REPLAY_READ_TRIES = 3

/** The event with the highest id, or null. */
const newestOf = (events: any[]): any => events.reduce<any>((top, e) => (top === null || e.id > top.id ? e : top), null)

/** #328 — a REST event (GET /events/) in the shape the mutes read (`camera_id`); a critical event someone
 *  acknowledged meanwhile is a plain event now: the normal mutes, toast and chime, never a critical announcement. */
function asAnnounceable(e: any): any {
  const event = { ...e, camera_id: e.camera_id ?? e.camera }
  return isCriticalEvent(event) && (event.acknowledged_at || event.acknowledged_by) ? { ...event, is_critical: false } : event
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { addNotification } = useNotifications()
  const queryClient = useQueryClient()

  // Keep a stable ref so the onMessage callback always calls the latest addNotification
  const addNotifRef = useRef(addNotification)
  useEffect(() => { addNotifRef.current = addNotification }, [addNotification])

  // #138 — the user's push categories, used to suppress the in-app bell for muted
  // events. The WS message itself is NOT filtered server-side (useWebSocket still
  // invalidates the event AND camera caches off every message, so muting a category
  // must not leave the dashboard stale) — only the bell/peek/sound is gated here.
  // CP 159 — the CAMERA's own category filter also gates the bell, ANDed with the
  // account's, so the in-app bell agrees with what FCM actually sent.
  // CP 164 — cameras whose owner switched push OFF. The server sends no FCM for these at
  // all, so the bell must not announce them either.
  // #337 — the same two cached queries and derivation, now in usePushMutes (the Dashboard's muted mark reads it too)
  // CP 391 — and the person's own camera rows ("My phone alerts"): null while they load (the bell rings then)
  const { prefs: pushPrefs, cameraFilters, mutedCameraIds, myCameraFilters } = usePushMutes()
  const prefsRef = useRef(pushPrefs)
  useEffect(() => { prefsRef.current = pushPrefs }, [pushPrefs])
  const cameraFiltersRef = useRef(cameraFilters)
  const mutedCamerasRef = useRef(mutedCameraIds)
  const myCameraFiltersRef = useRef(myCameraFilters)
  useEffect(() => {
    cameraFiltersRef.current = cameraFilters
    mutedCamerasRef.current = mutedCameraIds
    myCameraFiltersRef.current = myCameraFilters
  }, [cameraFilters, mutedCameraIds, myCameraFilters])

  // #297 — a desktop notification's click opens its event the way a toast click does; the handler runs
  // later, outside React, so it reads the latest router location through a ref.
  const navigate = useNavigate()
  const location = useLocation()
  const openEventRef = useRef<(eventId: number) => void>(() => {})
  useEffect(() => {
    openEventRef.current = (eventId) => {
      const { to, options } = eventOpenTarget(location, eventId)
      navigate(to, options)
    }
  }, [navigate, location])

  // #295 — the chime needs one gesture on the page before the browser lets it play; listen for it
  // until it works (the header shows a "sound off" button meanwhile).
  useEffect(() => installAlertSoundUnlock(), [])

  // #336 — a new dashboard build was deployed: one persistent "Update" toast (never a reload by itself). Only a bundle
  // with a build id checks (the production image bakes one in; the Vite dev server on stage has none).
  useEffect(() => {
    const current = import.meta.env.VITE_BUILD_ID
    if (!current) return
    return startBuildWatch({ current, fetchJson: fetchVersionJson, onNewBuild: () => showNewBuildToast() })
  }, [])

  // #294 redesign (CP 294 edge 13, Review Focus 4) — a logout stops the sound at once in every tab of this
  // browser. Any sign-out of this tab: stop the critical sound and give the sound leadership up. Only the
  // user's own logout tells the other tabs (logoutAndEndSession). #334 — a sign-out the API client forces (the
  // server refused the refresh token every tab shares) is not announced; it reaches the other tabs through the
  // stored login copy (authStore's storage listener), like any logout. Another tab announced a logout: the same
  // here, plus the app's normal local logout (the cache is cleared, so the alert bar, tab title and icon go; the
  // route sends this tab to the login page). #334 fix round 1 (review I1) — every sign-out of this tab, whatever
  // its source (the storage event, a removed copy, the API client, the announcement), clears the query cache:
  // the next user who signs in on this browser never sees this one's data.
  const signedIn = useRef(isAuthenticated)
  // #328 (round 4, R1; round 5) — the floor: the newest event id this tab handled (announced, muted or summarised;
  // live, replay or poll) or started from. The poll and the replay announce only ids above it, and it only moves
  // up, so an old event never comes back as new once the dedupe set below has forgotten it. -1: nothing yet.
  const floorRef = useRef(-1)
  // #328 — the page has started: the poll's first snapshot or the page-start request set the floor (silently only when
  // no floor exists yet); a new sign-in starts again
  const startedRef = useRef(false)
  // #328 — bumped at sign-out: a request answered after it belongs to the old session and is ignored
  const sessionRef = useRef(0)
  // #328 (round 4, R4) — the deaf-socket evidence: the ids the socket delivered; the ids the poll found first and
  // when (this tab's clock); when the socket last came up; a replay is running; the last deaf-socket restart
  const socketIdsRef = useRef<Set<number>>(new Set())
  const pollFirstSeenRef = useRef<Map<number, number>>(new Map())
  const connectedAtRef = useRef(0)
  const replayingRef = useRef(false)
  const lastDeafRestartRef = useRef(0)
  // #328 — the hook's status and restart, read by the poll's judgement outside React's render
  const statusRef = useRef<LiveStatus | undefined>(undefined)
  const restartRef = useRef<((reason: string) => void) | undefined>(undefined)
  useEffect(() => {
    if (signedIn.current && !isAuthenticated) {
      queryClient.clear()   // #334 — a second clear after the announcement or a local logout is harmless
      stopCriticalSound()
      stopLeadership()
      sessionRef.current++
      floorRef.current = -1
      startedRef.current = false
      socketIdsRef.current.clear()
      pollFirstSeenRef.current.clear()
      connectedAtRef.current = 0
      replayingRef.current = false
    }
    signedIn.current = isAuthenticated
  }, [isAuthenticated, queryClient])
  useEffect(() => onLogoutAnnounced(() => {
    stopCriticalSound()
    stopLeadership()
    const { isAuthenticated: stillIn, logout } = useAuthStore.getState()
    if (!stillIn) return
    queryClient.clear()
    logout()
  }), [queryClient])

  // #294 redesign — the sound leader's own WebSocket may be down while another tab's works: another tab
  // that saw a critical notification says so, and the leader refreshes its open list at once (an
  // in-flight fetch is reused, not restarted) instead of waiting for its next 30 s poll.
  useEffect(() => onCriticalSeen(() => {
    if (isSoundLeader()) queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() }, { cancelRefetch: false })
  }), [queryClient])

  // #328 (owner ruling b) — the chime across tabs. Any tab that announces an event asks for its chime
  // (requestChime, in announce below); the sound leader plays it here, once per event, even when its own
  // WebSocket is the dead one. A tab that is not the leader never plays, but every tab records the event first:
  // a tab that becomes the leader later never chimes again for an event the old leader played. #294 redesign —
  // at the user's chime volume, never when the user switched it off, and not while a critical alert is sounding.
  useEffect(() => {
    const chimed = new Set<number>()
    return onChimeRequested((eventId) => {
      if (chimed.has(eventId)) return
      chimed.add(eventId)
      if (chimed.size > CHIMED_IDS_KEPT) chimed.delete(chimed.values().next().value as number)
      if (!isSoundLeader()) return
      const { chimeEnabled, chimeVolume } = useDisplayPrefs.getState()
      if (chimeEnabled && !isCriticalSounding()) playAlertSound(chimeVolume / 100)
    })
  }, [])

  // #328 (round 5) — the floor moves up to this id (never down)
  const raiseFloor = (id: unknown) => {
    if (typeof id === 'number' && id > floorRef.current) floorRef.current = id
  }
  // #328 — event ids this tab has handled (announced, muted or summarised), so no event is handled twice; round 5:
  // every handled id raises the floor (the floor is the newest handled id), so a stream of muted events never looks
  // like missed ones once this set has forgotten them.
  const handledIdsRef = useRef<Set<number>>(new Set())
  const noteHandled = (id: unknown) => {
    if (typeof id !== 'number') return
    raiseFloor(id)
    const ids = handledIdsRef.current
    ids.add(id)
    if (ids.size > HANDLED_IDS_KEPT) ids.delete(ids.values().next().value as number)
  }
  // #328 (round 4, R4) — the socket delivered this event (whatever this tab then decided)
  const noteSocketDelivered = (id: unknown) => {
    if (typeof id !== 'number') return
    const ids = socketIdsRef.current
    ids.add(id)
    if (ids.size > SOCKET_IDS_KEPT) ids.delete(ids.values().next().value as number)
  }
  // #328 (round 4, R1) — announced: the tab's sessionStorage marker (part B) remembers it, and the floor moves up
  const markAnnounced = (event: any) => {
    rememberAnnounced(event)
    raiseFloor(event?.id)
  }
  // #328 (round 4, R4) — the socket (re)connected: a quiet window starts, and what the poll found first on the old
  // socket is no evidence against the new one
  const socketUp = () => {
    connectedAtRef.current = Date.now()
    pollFirstSeenRef.current.clear()
  }

  // #328 — what this tab does with one event. A muted category or camera announces nothing; a critical event
  // passes every mute.
  const decide = (event: any): AnnounceDecision => {
    // #294 — a critical event (the server's is_critical, nothing else) passes every mute below.
    if (isCriticalEvent(event)) return 'critical'
    // #138 — a muted category still reaches the event list and the camera list
    // (useWebSocket already invalidated those caches); it just does not announce
    // itself with a bell, a peek card, or a sound. CP 164 — nor a camera whose push is off.
    // #337 — muteReason is the one rule (shouldRingBell === no reason), and it names the camera switch
    const reason = muteReason(event, prefsRef.current, cameraFiltersRef.current, mutedCamerasRef.current, myCameraFiltersRef.current)
    if (reason) return reason === 'camera-off' ? 'muted-camera' : 'muted-category'
    return 'shown'
  }

  // #328 — the one decision for a live message, a replayed one and a polled one: the mutes, the critical rule,
  // the toast, the chime from the sound leader, the desktop alert. Returns the decision.
  const announce = (event: any, { via }: { via: 'live' | 'replay' | 'poll' }): AnnounceDecision | 'duplicate' => {
    // #328 — already handled by this tab (whichever path): never twice
    if (typeof event?.id === 'number' && handledIdsRef.current.has(event.id)) {
      liveLog('message', { id: event.id, decision: 'duplicate', via })
      return 'duplicate'
    }
    noteHandled(event?.id)
    const decision = decide(event)
    const critical = decision === 'critical'
    liveLog('message', { id: event?.id, decision, leader: isSoundLeader(), soundReady: isAlertSoundReady(), via })
    // #294 redesign — tell the other tabs (the sound leader refreshes its open list at once)
    if (critical) announceCriticalSeen()
    if (decision === 'muted-category' || decision === 'muted-camera') return decision
    // #295 — one decision: the toast (and with it the map pin) and the chime go together.
    // #294 — a critical event gets no chime: the critical sound plays instead (the critical sound
    // controller, once the open-critical list has it).
    // #294 redesign — the chime plays from the sound leader tab only (one per browser); #328 — this tab asks for
    // it, whichever tab is the leader (the effect above plays it there).
    addNotifRef.current(event)
    // #328 — remembered: the floor moves up, so a reconnect or the poll announces only what came after it
    markAnnounced(event)
    if (!critical && typeof event?.id === 'number') requestChime(event.id)
    // #297 — the same alert on the desktop (system notification, system sound) when the toast and the
    // chime are not enough: the page is not in front, or the sound is still blocked (after a reload,
    // until a click — the critical sound too). Only if the user allowed it (header "Desktop alerts").
    // #294 — a critical one stays on screen until clicked (desktopAlertOf marks it).
    if (shouldShowDesktopAlert(isAlertSoundReady())) {
      const content = desktopAlertOf(event)
      const eventId = content.eventId
      showDesktopAlert({
        ...content,
        onOpen: eventId === undefined ? undefined : () => openEventRef.current(eventId),
      })
    }
    return decision
  }

  // #328 — events the socket did not hand over (a replay or the poll, round 4 R3), oldest first, each through the
  // same decision as a live message. Part B's cap: a critical event is always announced on its own; normal ones up
  // to REPLAY_TOAST_CAP toasts, the rest only in one summary toast, which counts every event that passes the mutes
  // (muted ones are never counted). The newest moves the floor.
  const announceBatch = (events: any[], via: 'replay' | 'poll') => {
    let toasts = 0
    let summarised = 0
    let counted = 0
    let critical = false
    for (const event of events) {
      const decision = decide(event)
      if (decision !== 'muted-category' && decision !== 'muted-camera') counted++
      if (decision === 'shown' && toasts >= REPLAY_TOAST_CAP) {
        summarised++
        noteHandled(event.id)   // announced in the summary: its live message must not toast it again
        continue
      }
      const result = announce(event, { via })   // a muted one only logs its decision
      if (result === 'shown') toasts++
      if (result === 'critical') critical = true
    }
    const capped = summarised > 0
    if (capped) {
      toast.info(i18next.t('live.missedSummary', { ns: 'common', count: counted }), { id: 'live-missed' })
    }
    const newest = newestOf(events)
    if (newest) markAnnounced(newest)
    return { counted, capped, critical }
  }

  // #328 — a poll answer: everything on the page above the floor that this tab has not handled, oldest first,
  // under the cap. Returns those events (the deaf-socket judgement wants them).
  const announceFresh = (results: any[]) => {
    const fresh = pickMissed(results, { id: floorRef.current, occurred_at: null })
      .filter((e) => !handledIdsRef.current.has(e.id))
      .map(asAnnounceable)
    if (fresh.length > 0) {
      const { critical } = announceBatch(fresh, 'poll')
      refreshAfterMissed(critical)
    }
    return fresh
  }

  // #328 — the page's first answer (the poll's, or the page-start request's). With no floor at all, the page starts
  // at its newest event and nothing is announced. A live event has already set the floor (round 5: the page was
  // live before these events): it starts there, and what came after the floor is announced like any poll answer.
  // Idempotent: the poll's first snapshot and the page-start request may both call it with the same answer.
  const startPage = (results: any[]): number | null => {
    startedRef.current = true
    if (floorRef.current < 0) {
      const newest = newestOf(results)
      if (newest) markAnnounced(newest)
    } else {
      announceFresh(results)
    }
    return floorRef.current >= 0 ? floorRef.current : null
  }

  // #328 (round 4, R2/R5) — the newest page of the Dashboard's list: the poll's own answer when it is on its way or
  // still fresh, else one request through the same query (so the poll and the Dashboard's rows see it too).
  const recentPage = () => queryClient.fetchQuery({
    queryKey: queryKeys.events.recent(),
    queryFn: fetchRecentEvents,
    staleTime: queryOptions.events.recent.staleTime,
  })

  // #328 (round 5) — the replay's read: the request it joined can be replaced while it waits (a live message or an
  // acknowledgement refreshes the list; React Query cancels the old request and starts a new one, which this joins; with none on
  // its way it ends), a bounded number of times.
  const recentPageForReplay = async () => {
    for (let attempt = 1; ; attempt++) {
      try {
        return await recentPage()
      } catch (error) {
        if (!isCancelledError(error) || attempt >= REPLAY_READ_TRIES) throw error
        // join a replacement only: with none on its way (cache cleared at sign-out, an optimistic update reverted
        // the list) the replay ends here like on any other failure, and starts no request of its own
        if (queryClient.getQueryState(queryKeys.events.recent())?.fetchStatus !== 'fetching') throw error
        liveLog('replay', { readReplaced: attempt })
      }
    }
  }

  // #328 — the socket is back after a drop: announce what this tab missed, oldest first, from the newest page of
  // events (the id decides, not the capture time: a late upload would otherwise be left out). Nothing to compare
  // with yet (the page has not started and nothing was announced): nothing to replay.
  const replayMissed = async () => {
    if (!startedRef.current && floorRef.current < 0) return
    const session = sessionRef.current
    // the floor as the socket came back: a live event announced while the request is on its way must not hide
    // older events the drop swallowed (the dedupe set skips the live one itself)
    const floor = floorRef.current
    replayingRef.current = true
    try {
      let results: any[]
      try {
        results = (await recentPageForReplay()).results ?? []
      } catch (error) {
        console.error('[WebSocket] Could not read the events missed while disconnected:', error)
        return
      }
      if (sessionRef.current !== session) return
      const missed = pickMissed(results, { id: floor, occurred_at: null })
        .filter((e) => !handledIdsRef.current.has(e.id))
        .map(asAnnounceable)
      if (missed.length === 0) return
      const { counted, capped } = announceBatch(missed, 'replay')
      liveLog('replay', { count: missed.length, announced: counted, capped })
      // the recent rows and the open-critical list were refreshed when the socket opened
      refreshAfterMissed(false)
    } finally {
      replayingRef.current = false
    }
  }

  // #328 — the same lists a live event refreshes (useWebSocket), after events the socket did not hand over
  const refreshAfterMissed = (critical: boolean) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.events.unreadCount() })
    queryClient.invalidateQueries({ queryKey: queryKeys.events.total() })
    queryClient.invalidateQueries({ queryKey: ['events', 'paginated'] })
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread() })
    queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
    queryClient.invalidateQueries({ queryKey: queryKeys.cameras.mapPins() })
    if (critical) queryClient.invalidateQueries({ queryKey: queryKeys.events.criticalOpen() })
  }

  // #328 — the page's first connection: a marker left by an earlier page (sessionStorage outlives a reload) is
  // dropped, and this page starts from the newest event, so a replay covers only drops within this page's life.
  // The poll's first snapshot is the same information (one events request per page load): already answered, its
  // start stands; still on its way, it is awaited; failed, one request of its own (round 4 R7: also when the
  // failure was still on its way at connect time). If that fails too, a live event sets the floor, and the first
  // answer that works announces what came after it.
  const startPageMarker = async () => {
    forgetAnnounced()
    if (startedRef.current) {
      if (floorRef.current >= 0) rememberAnnounced({ id: floorRef.current, occurred_at: null })
      liveLog('replay', { pageStart: floorRef.current, from: 'poll' })
      return
    }
    const session = sessionRef.current
    const onItsWay = queryClient.getQueryState(queryKeys.events.recent())?.fetchStatus === 'fetching'
    let page = await recentPage().catch(() => null)
    if (page === null && onItsWay && sessionRef.current === session) page = await recentPage().catch(() => null)
    if (sessionRef.current !== session) return
    if (page === null) {
      liveLog('replay', { pageStart: null })
      return
    }
    liveLog('replay', { pageStart: startPage(page.results ?? []) })
  }

  // #328 (round 4, R4) — the deaf-socket evidence, judged on every poll answer, by this tab's own clock only. An
  // event the poll found first (fresh) waits; the next answer at least DEAF_EVIDENCE_WAIT_MS later judges it once:
  // still on the page and never delivered by the socket, it is evidence; gone from the page, it cannot be judged.
  // Nothing is judged or recorded while the socket is not live, a replay is running, or within CONNECT_QUIET_MS of
  // a (re)connect. Evidence restarts the socket, at most once per DEAF_RESTART_GAP_MS.
  const judgeDeafSocket = (results: any[], fresh: any[]) => {
    const now = Date.now()
    const candidates = pollFirstSeenRef.current
    if (statusRef.current !== 'live' || replayingRef.current || now - connectedAtRef.current < CONNECT_QUIET_MS) {
      candidates.clear()
      return
    }
    const shown = new Set<number>(results.map((e) => e.id))
    const evidence: number[] = []
    for (const [id, firstSeenAt] of Array.from(candidates)) {
      if (!shown.has(id)) { candidates.delete(id); continue }
      if (now - firstSeenAt < DEAF_EVIDENCE_WAIT_MS) continue
      candidates.delete(id)
      if (!socketIdsRef.current.has(id)) evidence.push(id)
    }
    for (const event of fresh) {
      candidates.set(event.id, now)
      if (candidates.size > POLL_FIRST_KEPT) candidates.delete(candidates.keys().next().value as number)
    }
    if (evidence.length === 0) return
    const restart = now - lastDeafRestartRef.current >= DEAF_RESTART_GAP_MS
    liveLog('deaf-socket', { ids: evidence, restart })
    if (restart) {
      lastDeafRestartRef.current = now
      restartRef.current?.('deaf socket')
    }
  }

  const websocket = useWebSocket({
    enabled: isAuthenticated,
    onMessage: (message) => {
      if (message.type === 'notification') {
        console.log('[WebSocket] Event notification received:', message.event)
        noteSocketDelivered(message.event?.id)
        announce(message.event, { via: 'live' })
      }
    },
    onReconnected: () => { socketUp(); void replayMissed() },
    onFirstConnected: () => { socketUp(); void startPageMarker() },
    onError: (error) => {
      console.error('[WebSocket] Connection error:', error)
    },
  })

  useEffect(() => {
    statusRef.current = websocket.status
    restartRef.current = websocket.restart
  })

  // #328 (owner ruling, round 3) — the poll as a second announcer, on every page. The Dashboard's recent-events
  // query (same key, request and options, so the two share one request), kept alive here and polled in background
  // tabs too. Its first snapshot of a session only marks where this page starts (silent when no floor exists yet, like
  // the first connect). After that, an event above the floor that this tab has not handled is announced exactly like a live
  // one, oldest first, never twice, under part B's cap (round 4 R3). A deaf socket (its subscription died on the
  // server) is found by judgeDeafSocket above and restarted; the restart's own replay skips what is announced here.
  // The Dashboard's list leaves motion-only events out, so the poll never announces those.
  // dataUpdatedAt is read too: an answer identical to the last one keeps the same data reference (structural
  // sharing), and the judgement must still run on every poll.
  const { data: recentEvents, dataUpdatedAt: recentUpdatedAt } = useQuery({
    queryKey: queryKeys.events.recent(),
    queryFn: fetchRecentEvents,
    enabled: isAuthenticated,
    ...queryOptions.events.recent,
  })
  useEffect(() => {
    if (!recentEvents) return
    const results: any[] = recentEvents.results ?? []
    if (!startedRef.current) {
      startPage(results)
      return
    }
    judgeDeafSocket(results, announceFresh(results))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEvents, recentUpdatedAt])

  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider')
  }
  return context
}
